export type Vec3 = [
    number,
    number,
    number
];
/** Traversal object types, indexed by `Traversal.type` (the C++ sim's object type). */
export const TRAVERSAL_TYPE_NAMES = ['Recovery', 'Branch', 'Vine', 'Flexible branch', 'Bounce leaf', 'Tree', 'Water', 'Dead branch'] as const;
/** A breakable branch: catchable, snaps after ~0.7 s of load, regrows after 8 s (Design Bible §6). */
export const DEAD_BRANCH = 7;
export type Traversal = {
    id: number;
    uuid: string;
    type: number;
    a: Vec3;
    b: Vec3;
    radius: number;
    name: string;
    tags: string[];
    region: string;
    art?: string;
    locked?: boolean;
    generatedFor?: string;
    generationSlot?: number;
};
export type EntityMeta = { uuid?: string; tags?: string[]; parent?: string; priority?: number; locked?: boolean };
export type Region = EntityMeta & {
    id: string;
    name: string;
    center: Vec3;
    radius: number;
    biome: string;
    color: string;
    volume?: string;
};
export type POI = EntityMeta & {
    id: string;
    name: string;
    position: Vec3;
    region: string;
    radius: number;
    description: string;
    category?: string;
    requires?: string[];
    vista?: { position: Vec3; target: Vec3; fov: number };
};
export type VolumeShape = 'box' | 'sphere' | 'capsule' | 'cylinder' | 'prism' | 'spline' | 'convex' | 'composite';
export type Volume = EntityMeta & { id: string; name: string; shape: VolumeShape; position: Vec3; size: Vec3; purpose: string; region: string; points?: Vec3[]; members?: string[] };
export type Checkpoint = EntityMeta & { id: string; name: string; position: Vec3; region: string; state?: number[]; physicsVersion: number };
export type Activity = EntityMeta & { id: string; name: string; gates: Vec3[]; radius: number; medals: number[]; region?: string; start?: Vec3 };
export type Route = {
    id: string;
    name: string;
    points: Vec3[];
    proven: boolean;
    physicsVersion: number;
    trace?: number[][];
    stale?: boolean;
    inputs?: MoveInput[];
    initialState?: number[];
    finalState?: number[];
    purpose?: string;
};
export type WorldData = {
    version: number;
    physicsVersion: number;
    id: string;
    name: string;
    seed: number;
    spawn: Vec3;
    objects: Traversal[];
    regions: Region[];
    pois: POI[];
    fruit: {
        id: string;
        position: Vec3;
        kind: string;
        region: string;
    }[];
    routes: Route[];
    npcs: {
        id: string;
        name: string;
        character: string;
        route: string;
        role: string;
    }[];
    activities: Activity[];
    volumes: Volume[];
    checkpoints?: Checkpoint[];
    tagRegistry?: string[];
};
export type SimState = {
    p: Vec3;
    v: Vec3;
    anchor: Vec3;
    radius: number;
    time: number;
    mode: number;
    grab: number;
    candidate: number;
    grabs: number;
    tails: number;
    falls: number;
    chain: number;
    bestChain: number;
    score: number;
    flex: number;
    autoTails: number;
    missReason: number;
    occluded: number;
    support: number;
    gripT: number;
    /** Load on a held/stood-on dead branch toward its snap (0-1). */
    strain: number;
    raw: number[];
};
export type MoveInput = {
    x: number;
    z: number;
    buttons: number;
    assist: number;
};
export type Settings = {
    assist: number;
    character: string;
    quality: string;
    volume: number;
    sensitivity: number;
    invertY: boolean;
    reducedMotion: boolean;
    accessory: string;
    guidance?: boolean;
};
export type Progress = {
    fruit: string[];
    discoveries: string[];
    bests: Record<string, number>;
    ghosts: Record<string, number[][]>;
    completed: string[];
    settings: Settings;
};
export const distance = (a: number[], b: number[]) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
